import {
  SuccessToast,
  cancelScheduledPause,
  getScheduledPauses,
  reportError,
} from '@vgrubs/components';
import { ScheduledPause } from 'components/src/types/api/location.types';
import { Provider } from 'components/src/types/shared.types';
import { useCallback, useEffect, useMemo, useState } from 'react';

/** The cron starting them runs every minute, so a pause is a minute late at most. */
const AFTER_START_MS = 65_000;

/**
 * The pauses set to start later on one screen of Manage Providers. The list
 * is the location's (with every brand's in it), so a brand screen asks with
 * the brand's location and keeps its own. `brandId` is null on the location
 * and on its own brand: the backend sends both as null.
 *
 * Nothing tells a merchant client when a pause starts (the admin server
 * starts it, and only admins get its `provider_changes`), so the list and
 * the statuses are fetched again just after the soonest start, through
 * `onStarted`.
 */
export const useScheduledPauses = ({
  locationId,
  brandId,
  onStarted,
}: {
  locationId: string | number;
  brandId: number | null;
  onStarted: () => void;
}) => {
  const [all, setAll] = useState<ScheduledPause[]>([]);

  const refresh = useCallback(async () => {
    if (!locationId || locationId === '0') return;
    try {
      const { data } = await getScheduledPauses(locationId);
      setAll(data.data);
    } catch (error) {
      reportError(error);
    }
  }, [locationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pauses = useMemo(
    () => all.filter((p) => (p.brandId ?? null) === brandId),
    [all, brandId]
  );

  const nextStart = useMemo(() => {
    const starts = pauses.map((p) => new Date(p.startAt).getTime());
    return starts.length ? Math.min(...starts) : null;
  }, [pauses]);

  useEffect(() => {
    if (nextStart === null) return;
    const timer = setTimeout(() => {
      refresh();
      onStarted();
    }, Math.max(nextStart - Date.now(), 0) + AFTER_START_MS);
    return () => clearTimeout(timer);
  }, [nextStart, refresh]);

  /**
   * Every answer but success (gone, cancelled already, started already) means
   * the list was stale, so it is fetched again either way, and the statuses
   * too, in case the pause is the one now running.
   */
  const cancel = async (pause: ScheduledPause) => {
    try {
      const { data } = await cancelScheduledPause(locationId, pause.id);
      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
      onStarted();
    }
    await refresh();
  };

  return {
    /** One provider's, or Pause All's with `null`. */
    of: (provider: Provider | null) =>
      pauses.filter((p) => (p.provider ?? null) === provider),
    refresh,
    cancel,
  };
};

export type ScheduledPausesState = ReturnType<typeof useScheduledPauses>;
